import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useAuth } from '../AuthContext';
import { OPERATIONS_TABS } from '../config/pages';
import FinalFilesSection from '../components/FinalFilesSection';

// ── Platform config — matches blueprint SCH_PLATFORMS exactly ────────────────
const SCH_PLATFORMS = [
  { id:'ig',  name:'Instagram',       color:'#e1306c', bg:'#fce4ec',
    native: (cap) => 'https://www.instagram.com/',
    help: 'IG requires manual upload — opens IG ready for posting. Caption already in your clipboard.' },
  { id:'fb',  name:'Facebook',        color:'#1877f2', bg:'#e7f0fd',
    native: (cap) => `https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(cap||'')}`,
    help: 'FB share dialog opens pre-filled with caption.' },
  { id:'wa',  name:'WhatsApp Status', color:'#25d366', bg:'#e6f7ec',
    native: (cap) => `https://api.whatsapp.com/send?text=${encodeURIComponent(cap||'')}`,
    help: 'Opens WhatsApp Web with caption ready.' },
  { id:'li',  name:'LinkedIn',        color:'#0a66c2', bg:'#e1ecf7',
    native: (cap) => `https://www.linkedin.com/sharing/share-offsite/?summary=${encodeURIComponent(cap||'')}`,
    help: 'LinkedIn share dialog opens pre-filled.' },
  { id:'yt',  name:'YouTube',         color:'#ff0000', bg:'#fde7e7',
    native: () => 'https://studio.youtube.com/channel/upload',
    help: 'Opens YouTube Studio upload page.' },
  { id:'x',   name:'X / Twitter',    color:'#000000', bg:'#e7e7e7',
    native: (cap) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(cap||'')}`,
    help: 'X compose opens with caption.' },
  { id:'pi',  name:'Pinterest',       color:'#bd081c', bg:'#fbe1e3',
    native: (cap) => `https://pinterest.com/pin/create/button/?description=${encodeURIComponent(cap||'')}`,
    help: 'Pinterest pin creator opens pre-filled.' },
  { id:'th',  name:'Threads',         color:'#000000', bg:'#e7e7e7',
    native: (cap) => `https://www.threads.net/intent/post?text=${encodeURIComponent(cap||'')}`,
    help: 'Threads compose opens with caption.' },
  { id:'gg',  name:'Google Ads',      color:'#4285f4', bg:'#e8f0fe',
    native: () => 'https://ads.google.com/',
    help: 'Opens Google Ads dashboard.' },
  { id:'ma',  name:'Meta Ads',        color:'#0668e1', bg:'#e7f0fd',
    native: () => 'https://business.facebook.com/adsmanager/',
    help: 'Opens Meta Ads Manager.' },
  { id:'tk',  name:'TikTok',          color:'#010101', bg:'#f0f0f0',
    native: () => 'https://www.tiktok.com/upload/',
    help: 'Opens TikTok upload page.' },
  { id:'tg',  name:'Telegram',        color:'#229ed9', bg:'#e3f4fc',
    native: (cap) => `https://t.me/share/url?text=${encodeURIComponent(cap||'')}`,
    help: 'Opens Telegram share.' },
];

const SCH_DASHBOARDS = {
  Instagram: 'https://business.facebook.com/creatorstudio/home',
  Facebook:  'https://business.facebook.com/creatorstudio/home',
  LinkedIn:  'https://www.linkedin.com/',
  YouTube:   'https://studio.youtube.com/',
};

const normStr = s => String(s||'').toLowerCase().replace(/[\s\/\._\-]/g,'');

function findPlatformConfig(name) {
  return SCH_PLATFORMS.find(pl => {
    const a = normStr(pl.name), b = normStr(name);
    return a === b || a.includes(b) || b.includes(a);
  });
}

function getAssignedTiles(wo) {
  const assigned = Array.isArray(wo.platforms) && wo.platforms.length ? wo.platforms : [];
  return SCH_PLATFORMS.filter(pl =>
    assigned.some(p => { const a=normStr(pl.name),b=normStr(p); return a===b||a.includes(b)||b.includes(a); })
  );
}

// Blueprint: schWOState — posted=all posted, scheduled=all at least scheduled, ready=any untouched
function getWOState(wo, platRecs) {
  const tiles = getAssignedTiles(wo);
  if (!tiles.length) return 'ready';
  let postedCount = 0, untouchedCount = 0;
  tiles.forEach(pl => {
    const ps = platRecs[pl.id] || platRecs[pl.name] || {};
    if (!ps?.state || ps.state === 'pending') { untouchedCount++; return; }
    if (ps.state === 'posted') postedCount++;
  });
  if (postedCount === tiles.length) return 'posted';
  if (untouchedCount === 0) return 'scheduled';
  return 'ready';
}

// Blueprint: schComposedCaption
function composeCaption(wo) {
  const pc = wo.post_copy;
  if (!pc) return '';
  return [pc.caption, pc.hashtags, pc.link].filter(Boolean).join('\n\n');
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

function isPastDue(scheduledAt) {
  return scheduledAt && new Date(scheduledAt) < new Date();
}

// ── Platform SVG Icons ────────────────────────────────────────────────────────
const PLAT_SVG = {
  ig:  <svg viewBox="0 0 24 24" width="20" height="20"><defs><radialGradient id="igS2" cx="30%" cy="107%" r="150%"><stop offset="0%" stopColor="#fdf497"/><stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/><stop offset="90%" stopColor="#285AEB"/></radialGradient></defs><path fill="url(#igS2)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
  fb:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#1877f2"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.532-4.697 1.312 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.956.93-1.956 1.887v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>,
  wa:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#25d366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>,
  li:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#0a66c2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  yt:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#ff0000"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  x:   <svg viewBox="0 0 24 24" width="20" height="20" fill="#000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  pi:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#bd081c"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>,
  th:  <svg viewBox="0 0 24 24" width="20" height="20" fill="#000"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.5 12.037c0-7.146 4.776-11.77 12.5-11.993C15.886.05 17.736.49 19.3 1.329l-1.13 2.048c-1.213-.669-2.683-1.02-4.246-.977C7.647 2.614 3.887 6.388 3.887 12.037c0 3.01.682 5.355 2.029 6.97 1.315 1.576 3.304 2.37 5.912 2.387 2.517.013 4.375-.55 5.674-1.721 1.302-1.174 1.966-2.869 1.965-5.038v-.017c0-.88-.108-1.67-.321-2.352H15.77a5.95 5.95 0 0 1 .177 1.476c0 1.655-.448 2.906-1.333 3.721-.87.802-2.11 1.2-3.688 1.186-1.73-.015-3.064-.526-3.965-1.52-.867-.957-1.307-2.38-1.307-4.229 0-3.738 2.079-5.813 5.854-5.813 1.056 0 2.11.218 3.13.648l.858-1.942c-1.26-.556-2.604-.838-3.989-.838-4.892 0-7.996 2.948-7.996 7.945 0 2.296.566 4.072 1.682 5.278 1.116 1.206 2.776 1.82 4.937 1.83.88.004 1.703-.086 2.452-.27V21.65c-.762.228-1.607.344-2.598.35z"/></svg>,
};

function PlatformIcon({ id, size=20 }) {
  return PLAT_SVG[id] || <span style={{fontSize:size*0.7,fontWeight:700}}>{(id||'?')[0].toUpperCase()}</span>;
}

// ── Batch Status Modal (blueprint: schOpenBatchModal) ─────────────────────────
function BatchModal({ wo, tiles, platRecs, onClose, onSave, currentTab }) {
  const [states, setStates] = useState(() => {
    const s = {};
    tiles.forEach(pl => {
      const ps = platRecs[pl.id] || platRecs[pl.name] || {};
      s[pl.id] = {
        state:       ps.state || 'none',
        scheduledFor:ps.scheduled_at || ps.scheduledFor || '',
        proof_url:   ps.proof_url    || ps.proofUrl     || ps.proof_screenshot_url || '',
        proof_image: ps.proof_image  || ps.proofImage   || ps.proof_screenshot     || '',
        url:         ps.live_url     || ps.url          || '',
      };
    });
    return s;
  });
  const [team,    setTeam]    = useState([]);
  const [user,    setUser]    = useState(localStorage.getItem('sch_last_user') || '');
  const [step,    setStep]    = useState('edit');
  const [pending, setPending] = useState([]);
  const [saving,  setSaving]  = useState(false);
  const fileRefs              = useRef({});

  useEffect(() => {
    api.get('/team').then(r => setTeam(r.data.data || r.data || [])).catch(() => {});
  }, []);

  const toggle = (plId, val) => setStates(prev => ({...prev,[plId]:{...prev[plId],state:val}}));

  const handleProofFile = (plId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setStates(prev => ({...prev,[plId]:{...prev[plId],proof_image:ev.target.result}}));
    reader.readAsDataURL(file);
  };

  const buildPending = () => {
    const errs = [], items = [];
    tiles.forEach(pl => {
      const s    = states[pl.id];
      const prev = (platRecs[pl.id] || platRecs[pl.name] || {}).state || 'none';
      if (s.state === prev) return;
      if (s.state === 'none') { items.push({plId:pl.id,plName:pl.name,action:'clear'}); return; }
      if (s.state === 'scheduled') {
        if (!s.scheduledFor) { errs.push(`${pl.name}: pick a date+time`); return; }
        if (!s.proof_url && !s.proof_image) { errs.push(`${pl.name}: proof required (URL or screenshot)`); return; }
        items.push({plId:pl.id,plName:pl.name,action:'schedule',
          payload:{when:s.scheduledFor,proofUrl:s.proof_url,proofImage:s.proof_image}});
      }
      if (s.state === 'posted') {
        if (!s.url || !/^https?:\/\//i.test(s.url)) { errs.push(`${pl.name}: needs a valid live URL`); return; }
        items.push({plId:pl.id,plName:pl.name,action:'post',payload:{url:s.url}});
      }
    });
    return { errs, items };
  };

  const goConfirm = () => {
    const { errs, items } = buildPending();
    if (errs.length) { alert('⚠️ ' + errs.join('\n')); return; }
    if (!items.length) { onClose(); return; }
    setPending(items); setStep('confirm');
  };

  const commit = async () => {
    if (!user.trim()) { alert('Please select your name for the audit trail.'); return; }
    localStorage.setItem('sch_last_user', user);
    setSaving(true);
    try {
      for (const ch of pending) {
        if (ch.action === 'schedule') {
          await api.post(`/scheduling/${wo.id}/platform/${encodeURIComponent(ch.plName)}/schedule`, {
            scheduled_at: ch.payload.when,
            proof_url:   ch.payload.proofUrl   || null,
            proof_image: ch.payload.proofImage || null,
          });
        } else if (ch.action === 'post') {
          await api.post(`/scheduling/${wo.id}/platform/${encodeURIComponent(ch.plName)}/post`, {
            live_url: ch.payload.url,
          });
        }
      }
      onSave();
    } finally { setSaving(false); }
  };

  const fmtWhen = s => {
    if (!s) return s;
    const [d,t] = s.split('T');
    if (!d) return s;
    const [Y,M,D] = d.split('-');
    return `${D}-${M}-${Y}${t?' at '+t:''}`;
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:14,width:700,maxWidth:'96vw',maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>

        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <div>
            <div style={{fontWeight:700,fontSize:'1rem',color:'var(--text1)'}}>
              {step==='edit' ? '📋 Update Posting Status' : '⚠️ Confirm Changes'}
            </div>
            <div style={{fontSize:'0.72rem',color:'var(--text3)',marginTop:2}}>
              {wo.wo_number} · {wo.title} · {wo.client?.name}
            </div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.3rem',cursor:'pointer',color:'var(--text3)'}}>✕</button>
        </div>

        {step === 'edit' ? (
          <>
            <div style={{padding:'8px 20px',fontSize:'0.74rem',color:'var(--text2)',background:'var(--bg3)',borderBottom:'1px solid var(--border)',flexShrink:0}}>
              {(currentTab === 'scheduled' || currentTab === 'posted')
                ? <>💡 Mark as <b>✓ Posted</b> once the post is live — paste the live URL as proof.</>
                : <>💡 Pick <b>🗓️ Scheduled</b> (set future date+time + proof) or <b>✓ Posted</b> (paste live URL). Leave <b>Not Yet</b> if pending.</>
              }
            </div>
            <div style={{overflowY:'auto',flex:1,padding:'14px 20px'}}>
              {tiles.map(pl => {
                const s = states[pl.id];
                const mkOpt = (val, label, color, checked) => (
                  <label style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',border:`1.5px solid ${checked?color:'var(--border2)'}`,borderRadius:6,cursor:'pointer',background:checked?`${color}18`:'var(--bg2)',fontSize:'0.78rem',fontWeight:700,color:checked?color:'var(--text2)'}}>
                    <input type="radio" name={`schst-${pl.id}`} value={val} checked={s.state===val}
                      onChange={()=>toggle(pl.id,val)} style={{accentColor:color}} />
                    {label}
                  </label>
                );
                return (
                  <div key={pl.id} style={{border:'1px solid var(--border)',borderRadius:10,padding:14,marginBottom:10,background:'var(--bg2)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                      <div style={{width:32,height:32,borderRadius:6,background:pl.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <PlatformIcon id={pl.id} size={20} />
                      </div>
                      <span style={{fontWeight:700,fontSize:'0.9rem',color:pl.color}}>{pl.name}</span>
                    </div>
                    <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
                      {(currentTab !== 'scheduled' && currentTab !== 'posted') && mkOpt('none', 'Not Yet', '#94a3b8', !s.state||s.state==='none')}
                      {(currentTab !== 'scheduled' && currentTab !== 'posted') && mkOpt('scheduled', '🗓️ Scheduled', '#3b7ef0', s.state==='scheduled')}
                      {mkOpt('posted',    '✓ Posted',      '#1db87a', s.state==='posted')}
                    </div>

                    {s.state === 'scheduled' && (
                      <div style={{marginTop:8}}>
                        {/* ── Read-only view in Scheduled/Posted tabs ── */}
                        {(currentTab === 'scheduled' || currentTab === 'posted') ? (
                          <div style={{
                            background:'var(--bg3)', border:'1px solid var(--border)',
                            borderRadius:8, padding:'10px 12px', display:'flex', flexDirection:'column', gap:8,
                          }}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <span style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',minWidth:100}}>🗓️ Go-live time</span>
                              <span style={{fontSize:'0.78rem',fontWeight:700,color:'#3b7ef0'}}>
                                {s.scheduledFor ? new Date(s.scheduledFor).toLocaleString() : '—'}
                              </span>
                            </div>
                            {s.proof_url && (
                              <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                                <span style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)',minWidth:100}}>🔗 Proof URL</span>
                                <a href={s.proof_url} target="_blank" rel="noreferrer"
                                  style={{fontSize:'0.73rem',color:'var(--accent)',wordBreak:'break-all',textDecoration:'underline'}}>
                                  {s.proof_url}
                                </a>
                              </div>
                            )}
                            {s.proof_image && (
                              <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                <span style={{fontSize:'0.68rem',fontWeight:700,color:'var(--text3)'}}>📷 Proof screenshot</span>
                                <img src={s.proof_image} alt="proof"
                                  style={{maxWidth:'100%',maxHeight:140,borderRadius:6,border:'1px solid var(--border)',objectFit:'contain'}} />
                              </div>
                            )}
                            {!s.proof_url && !s.proof_image && (
                              <div style={{fontSize:'0.72rem',color:'#9ca3af',fontStyle:'italic'}}>No proof submitted</div>
                            )}
                          </div>
                        ) : (
                          /* ── Editable in Ready to Schedule tab ── */
                          <>
                            <label style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text3)',display:'block',marginBottom:4}}>When to go live</label>
                            <input type="datetime-local" className="form-input" value={s.scheduledFor}
                              onChange={e=>setStates(prev=>({...prev,[pl.id]:{...prev[pl.id],scheduledFor:e.target.value}}))}
                              style={{marginBottom:10}} />
                            <label style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text3)',display:'block',marginBottom:4}}>
                              Proof <span style={{color:'#e87830',fontWeight:400}}>* required — paste URL OR upload screenshot</span>
                            </label>
                            <input type="text" className="form-input" placeholder="Scheduled-post URL or ID from the platform..."
                              value={s.proof_url}
                              onChange={e=>setStates(prev=>({...prev,[pl.id]:{...prev[pl.id],proof_url:e.target.value}}))}
                              style={{marginBottom:6}} />
                            <div style={{fontSize:'0.7rem',color:'var(--text3)',marginBottom:6,fontWeight:700}}>— OR — upload screenshot:</div>
                            {s.proof_image ? (
                              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                                <img src={s.proof_image} alt="proof" style={{maxWidth:'100%',maxHeight:160,borderRadius:4,border:'1px solid var(--border)'}} />
                                <button onClick={()=>setStates(prev=>({...prev,[pl.id]:{...prev[pl.id],proof_image:''}}))}
                                  style={{padding:'3px 10px',fontSize:'0.68rem',background:'#e84545',color:'#fff',border:'none',borderRadius:4,cursor:'pointer'}}>
                                  ✕ Remove
                                </button>
                              </div>
                            ) : (
                              <div onClick={()=>fileRefs.current[pl.id]?.click()}
                                style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:70,border:'2px dashed var(--border2)',borderRadius:8,cursor:'pointer',background:'var(--bg2)',flexDirection:'column',gap:4}}>
                                <div style={{fontSize:'1.4rem'}}>📷</div>
                                <div style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text2)'}}>Click to upload screenshot</div>
                              </div>
                            )}
                            <input ref={el=>fileRefs.current[pl.id]=el} type="file" accept="image/*" style={{display:'none'}}
                              onChange={e=>handleProofFile(pl.id,e.target.files?.[0])} />
                            {SCH_DASHBOARDS[pl.name] && (
                              <div style={{marginTop:6,fontSize:'0.7rem'}}>
                                <a href={SCH_DASHBOARDS[pl.name]} target="_blank" rel="noreferrer" style={{color:'var(--accent)',textDecoration:'underline'}}>
                                  🔗 Open {pl.name} scheduler dashboard
                                </a>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {s.state === 'posted' && (
                      <div style={{marginTop:8}}>
                        <label style={{fontSize:'0.72rem',fontWeight:700,color:'var(--text3)',display:'block',marginBottom:4}}>Live post URL</label>
                        <input type="url" className="form-input" placeholder="https://..."
                          value={s.url}
                          onChange={e=>setStates(prev=>({...prev,[pl.id]:{...prev[pl.id],url:e.target.value}}))} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:8,justifyContent:'flex-end',flexShrink:0}}>
              <button onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button onClick={goConfirm} className="btn btn-primary" style={{background:'#6c58f0',borderColor:'#6c58f0'}}>Review Changes →</button>
            </div>
          </>
        ) : (
          <>
            <div style={{overflowY:'auto',flex:1,padding:'14px 20px'}}>
              <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text2)',marginBottom:10}}>Changes to apply:</div>
              {pending.map(ch => {
                const bg = ch.action==='schedule'?'#3b7ef0':ch.action==='post'?'#1db87a':'var(--border2)';
                return (
                  <div key={ch.plId} style={{padding:'10px 12px',background:`${bg}08`,border:`1px solid ${bg}33`,borderRadius:8,marginBottom:8}}>
                    <div style={{fontWeight:700,color:bg,fontSize:'0.85rem'}}>
                      {ch.action==='schedule'?'🗓️ Schedule':ch.action==='post'?'✓ Mark Posted':'↺ Clear'} — {ch.plName}
                    </div>
                    {ch.action==='schedule' && ch.payload.when && (
                      <div style={{color:'var(--text2)',fontSize:'0.78rem',marginTop:2}}>
                        for <b>{fmtWhen(ch.payload.when)}</b>
                        {ch.payload.proofUrl && <span style={{marginLeft:8,fontSize:'0.68rem',color:'var(--text3)'}}>✓ Proof URL</span>}
                        {ch.payload.proofImage && <span style={{marginLeft:8,fontSize:'0.68rem',color:'var(--text3)'}}>✓ Screenshot</span>}
                      </div>
                    )}
                    {ch.action==='post' && (
                      <div style={{fontSize:'0.7rem',color:'var(--text3)',marginTop:2,wordBreak:'break-all'}}>{ch.payload.url}</div>
                    )}
                  </div>
                );
              })}
              <div style={{marginTop:14}}>
                <label style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text2)',display:'block',marginBottom:4}}>
                  Your name (audit trail) <span style={{color:'#e84545'}}>*</span>
                </label>
                <select className="form-select" value={user} onChange={e=>setUser(e.target.value)} style={{width:'100%'}}>
                  <option value="">— Select your name —</option>
                  {team.map(m=><option key={m.id} value={m.name}>{m.name}{m.role?` · ${m.role}`:''}</option>)}
                </select>
              </div>
            </div>
            <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:8,justifyContent:'flex-end',flexShrink:0}}>
              <button onClick={()=>setStep('edit')} className="btn btn-secondary">← Back</button>
              <button onClick={commit} disabled={saving} className="btn btn-primary" style={{background:'#6c58f0',borderColor:'#6c58f0'}}>
                {saving?'Saving…':'✓ Confirm & Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ── Image Lightbox ────────────────────────────────────────────────────────────
function ImageLightbox({ src, name, onClose }) {
  React.useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:99999,
        display:'flex',alignItems:'center',justifyContent:'center',cursor:'zoom-out'}}>
      <button onClick={onClose}
        style={{position:'absolute',top:16,right:16,padding:'6px 14px',
          background:'rgba(255,255,255,0.15)',color:'#fff',border:'none',
          borderRadius:6,cursor:'pointer',fontSize:'0.82rem',fontWeight:600}}>
        ✕ Close
      </button>
      <div style={{textAlign:'center'}} onClick={e=>e.stopPropagation()}>
        <img src={src} alt={name||'creative'}
          style={{maxWidth:'92vw',maxHeight:'88vh',borderRadius:8,
            objectFit:'contain',boxShadow:'0 8px 48px rgba(0,0,0,0.6)'}} />
        {name && <div style={{color:'rgba(255,255,255,0.6)',fontSize:'0.75rem',marginTop:8}}>{name}</div>}
      </div>
    </div>
  );
}

// ── WO Card (blueprint: schRenderCard) ────────────────────────────────────────
function SchedulingCard({ wo, onReload, currentTab }) {
  const [platRecs,    setPlatRecs]    = useState({});
  const [loadingDetail,setLoadingDetail] = useState(true);
  const [batchTarget, setBatchTarget] = useState(null);
  const [lightbox,    setLightbox]    = useState(false);

  useEffect(() => {
    api.get(`/scheduling/${wo.id}`)
      .then(r => {
        const map = {};
        (r.data.platforms || []).forEach(p => {
          const pl = findPlatformConfig(p.platform);
          const key = pl ? pl.id : p.platform;
          map[key] = { ...p, state: p.state === 'pending' ? undefined : p.state };
        });
        setPlatRecs(map);
      })
      .catch(()=>{})
      .finally(()=>setLoadingDetail(false));
  }, [wo.id]);

  const tiles     = getAssignedTiles(wo);
  const woState   = getWOState(wo, platRecs);
  const woColor   = woState==='posted'?'#1db87a':woState==='scheduled'?'#3b7ef0':'#f7c34f';
  const woLabel   = woState==='posted'?'✅ All Posted':woState==='scheduled'?'🗓️ Scheduled':'🚀 Ready to Schedule';
  const caption   = composeCaption(wo);
  const [ey,em,ed]= (wo.event_date||'').split('-');
  const isReady   = woState === 'ready' && currentTab === 'ready';

  const handleLaunch = (pl) => {
    if (caption) navigator.clipboard?.writeText(caption).catch(()=>{});
    const url = typeof pl.native==='function' ? pl.native(caption) : pl.native;
    window.open(url,'_blank');
  };

  const handleCopyCaption = () => {
    if (!caption) { alert('No approved post copy yet.'); return; }
    navigator.clipboard?.writeText(caption)
      .then(()=>alert('📋 Post copy copied — paste into the platform after launch'))
      .catch(()=>alert('Copy failed.'));
  };

  const orphans = (wo.platforms||[]).filter(p => !tiles.some(pl=>normStr(pl.name)===normStr(p)));

  // Blueprint: schApprovedAssets
  const creative = wo.creative?.status==='Approved' ? wo.creative : null;
  const postCopy = wo.post_copy;

  return (
    <>
      {/* ── Card ── */}
      <div style={{
        background:'var(--bg2)',
        border:'1px solid var(--border)',
        borderRadius:12,
        marginBottom:16,
        overflow:'hidden',
        boxShadow:'0 1px 4px rgba(0,0,0,0.06)',
      }}>
        {/* Accent top bar */}
        <div style={{height:4,background:woColor,width:'100%'}} />

        {/* ── Header ── */}
        <div style={{
          padding:'12px 18px',
          display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',
          borderBottom:'1px solid var(--border)',
          background:'var(--bg3)',
        }}>
          <span style={{fontFamily:'Space Mono,monospace',fontSize:'0.72rem',fontWeight:800,
            color:'var(--accent)',background:'rgba(59,126,240,0.08)',
            padding:'2px 8px',borderRadius:6,border:'1px solid rgba(59,126,240,0.2)'}}>
            {wo.wo_number}
          </span>
          {wo.client?.name && (
            <span style={{padding:'2px 10px',background:'var(--bg2)',
              border:'1px solid var(--border2)',borderRadius:20,
              fontSize:'0.7rem',fontWeight:700,color:'var(--text2)'}}>
              {wo.client.name}
            </span>
          )}
          <span style={{fontWeight:700,fontSize:'0.88rem',color:'var(--text1)'}}>{wo.title}</span>
          {wo.event_date && (
            <span style={{fontSize:'0.7rem',color:'var(--text3)',fontFamily:'Space Mono,monospace'}}>
              📅 {ed}-{em}-{ey}
            </span>
          )}
          {wo.campaign_type?.name && (
            <span style={{fontSize:'0.68rem',color:'var(--text3)',padding:'1px 6px',
              background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:4}}>
              {wo.campaign_type.name}
            </span>
          )}
          <span style={{marginLeft:'auto',padding:'4px 12px',
            background:`${woColor}18`,color:woColor,
            borderRadius:20,fontSize:'0.72rem',fontWeight:800,
            border:`1px solid ${woColor}44`,whiteSpace:'nowrap'}}>
            {woLabel}
          </span>
        </div>

        <div style={{padding:'16px 18px'}}>

          {/* ── Creative thumbnail — always visible ── */}
          <div style={{display:'flex',gap:14,alignItems:'flex-start',flexWrap:'wrap',marginBottom:14}}>
            <div style={{flexShrink:0,display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
              {creative?.image_data ? (
                <>
                  <img src={creative.image_data} alt="creative"
                    onClick={()=>setLightbox(true)}
                    style={{width:100,height:100,objectFit:'cover',borderRadius:8,
                      border:'2px solid #1db87a',
                      boxShadow:'0 2px 8px rgba(29,184,122,0.2)',cursor:'zoom-in'}} />
                  <span style={{fontSize:'0.6rem',fontWeight:700,color:'#1db87a'}}>✅ Approved</span>
                </>
              ) : (
                <div style={{width:100,height:100,borderRadius:8,
                  border:'2px dashed var(--border2)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  flexDirection:'column',gap:4,color:'var(--text3)'}}>
                  <span style={{fontSize:'1.6rem',opacity:0.3}}>🖼️</span>
                  <span style={{fontSize:'0.58rem',textAlign:'center',lineHeight:1.3}}>No approved creative</span>
                </div>
              )}
            </div>

            {/* ── Post copy — Ready tab only ── */}
            {isReady && postCopy?.caption ? (
              <div style={{flex:1,minWidth:220,
                background:'rgba(29,184,122,0.04)',
                border:'1px solid rgba(29,184,122,0.2)',
                borderRadius:8,padding:'10px 14px',
                cursor:'pointer',transition:'border-color 0.15s'}}
                onClick={handleCopyCaption}
                onMouseOver={e=>e.currentTarget.style.borderColor='rgba(29,184,122,0.5)'}
                onMouseOut={e=>e.currentTarget.style.borderColor='rgba(29,184,122,0.2)'}
                title="Click to copy post copy">
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                  <span style={{fontSize:'0.65rem',fontWeight:800,color:'#0f8050',
                    background:'rgba(29,184,122,0.1)',padding:'2px 8px',borderRadius:10}}>
                    ✅ APPROVED POST COPY
                  </span>
                  {postCopy.post_time && (
                    <span style={{fontSize:'0.65rem',color:'var(--text3)',fontFamily:'monospace'}}>⏰ {postCopy.post_time}</span>
                  )}
                  <span style={{marginLeft:'auto',fontSize:'0.65rem',fontWeight:700,
                    color:'var(--accent)',whiteSpace:'nowrap'}}>📋 Click to copy</span>
                </div>
                <div style={{fontSize:'0.82rem',color:'var(--text1)',
                  whiteSpace:'pre-wrap',maxHeight:72,overflow:'auto',lineHeight:1.5}}>
                  {postCopy.caption}
                </div>
                {postCopy.hashtags && (
                  <div style={{fontSize:'0.72rem',color:'#6c58f0',marginTop:6,lineHeight:1.4}}>
                    {postCopy.hashtags}
                  </div>
                )}
                {postCopy.link && (
                  <div style={{fontSize:'0.7rem',color:'var(--text3)',marginTop:4}}>🔗 {postCopy.link}</div>
                )}
              </div>
            ) : isReady ? (
              <div style={{flex:1,minWidth:220,
                background:'var(--bg3)',border:'1px dashed var(--border2)',
                borderRadius:8,padding:14,
                display:'flex',alignItems:'center',justifyContent:'center',
                color:'var(--text3)',fontSize:'0.78rem',gap:8}}>
                <span style={{opacity:0.5}}>📝</span> Post copy not yet approved
              </div>
            ) : null}
          </div>

          {/* ── Formats + Copy action — Ready tab only ── */}
          {isReady && ((wo.output_formats||[]).length > 0 || caption) && (
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',
              marginBottom:14,paddingBottom:14,borderBottom:'1px solid var(--border)'}}>
              {(wo.output_formats||[]).length > 0 && (
                <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',flex:1}}>
                  <span style={{fontSize:'0.65rem',fontWeight:700,color:'var(--text3)'}}>FORMATS</span>
                  {wo.output_formats.map((f,i)=>(
                    <span key={i} style={{padding:'2px 8px',
                      background:'rgba(108,88,240,0.06)',
                      border:'1px solid rgba(108,88,240,0.2)',
                      borderRadius:20,fontSize:'0.68rem',fontWeight:600,color:'#6c58f0'}}>
                      {f}
                    </span>
                  ))}
                </div>
              )}
              {caption && (
                <button onClick={handleCopyCaption}
                  style={{padding:'5px 14px',fontSize:'0.73rem',fontWeight:700,
                    background:'var(--accent)',color:'#fff',
                    border:'none',borderRadius:6,cursor:'pointer',
                    whiteSpace:'nowrap',flexShrink:0}}>
                  📋 Copy Post Copy
                </button>
              )}
            </div>
          )}

          {/* ── Hi-Res Final Files — Ready tab only ── */}
          {isReady && (
            <FinalFilesSection
              woId={wo.id}
              formats={wo.output_formats || []}
              isApproved={!!creative}
              schedulerView={true}
            />
          )}

          {/* ── Launch & Record ── */}
          <div style={{marginTop:16}}>
            {currentTab !== 'posted' && (
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                <span style={{fontSize:'0.68rem',fontWeight:800,
                  color:'var(--text3)',letterSpacing:'0.05em'}}>
                  🚀 LAUNCH & RECORD
                </span>
                <span style={{fontSize:'0.68rem',color:'var(--text3)',fontWeight:400}}>
                  — opens native compose; record scheduled date or live URL
                </span>
                <button onClick={()=>setBatchTarget('__all__')}
                  style={{marginLeft:'auto',padding:'5px 14px',fontSize:'0.72rem',
                    fontWeight:700,background:'#6c58f0',color:'#fff',
                    border:'none',borderRadius:6,cursor:'pointer',whiteSpace:'nowrap'}}>
                  📋 Update All Status
                </button>
              </div>
            )}

            {loadingDetail ? (
              <div style={{fontSize:'0.75rem',color:'var(--text3)',padding:'8px 0'}}>Loading…</div>
            ) : tiles.length > 0 ? (
              <div style={{display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:8}}>
                {tiles.map(pl => {
                  const ps       = platRecs[pl.id] || {};
                  const state    = ps.state;
                  const stateBg  = state==='posted'?'#1db87a':state==='scheduled'?'#3b7ef0':null;
                  const schedAt  = ps.scheduled_at || ps.scheduledFor;
                  const liveUrl  = ps.live_url || ps.url;
                  const proofUrl = ps.proof_url || ps.proofUrl;

                  return (
                    <div key={pl.id} style={{
                      borderRadius:10,overflow:'hidden',
                      border:stateBg?`1.5px solid ${stateBg}44`:'1px solid var(--border)',
                      background:stateBg?`${stateBg}06`:'var(--bg3)',
                      display:'flex',flexDirection:'column',
                    }}>
                      {/* Platform header */}
                      <div style={{display:'flex',alignItems:'center',gap:8,
                        padding:'8px 10px',
                        borderBottom:'1px solid var(--border)'}}>
                        <div style={{width:28,height:28,borderRadius:6,
                          background:pl.bg,flexShrink:0,
                          display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <PlatformIcon id={pl.id} size={18} />
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:'0.75rem',fontWeight:700,color:pl.color,
                            whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                            {pl.name}
                          </div>
                          {stateBg ? (
                            <div style={{fontSize:'0.6rem',fontWeight:800,color:stateBg}}>
                              {state==='posted'?'✅ Posted':'🗓️ Scheduled'}
                            </div>
                          ) : (
                            <div style={{fontSize:'0.6rem',color:'var(--text3)'}}>Pending</div>
                          )}
                        </div>
                      </div>

                      {/* Info */}
                      <div style={{padding:'6px 10px',fontSize:'0.62rem',color:'var(--text3)',
                        fontFamily:'Space Mono,monospace',lineHeight:1.5}}>
                        {schedAt && (
                          <div>
                            {isPastDue(schedAt) && (
                              <span style={{display:'inline-block',padding:'1px 6px',
                                background:'#ff5252',color:'#fff',borderRadius:6,
                                fontSize:'0.58rem',fontWeight:800,marginBottom:2}}>
                                ⚠ PAST DUE
                              </span>
                            )}
                            <div>🗓 {fmtDateTime(schedAt)}</div>
                            {(ps.scheduled_by||ps.scheduledBy) && (
                              <div style={{color:'var(--text3)'}}>by {ps.scheduled_by||ps.scheduledBy}</div>
                            )}
                          </div>
                        )}
                        {state==='posted' && (ps.posted_by||ps.postedBy) && (
                          <div>by {ps.posted_by||ps.postedBy}{(ps.posted_at||ps.postedAt)?` · ${fmtDateTime(ps.posted_at||ps.postedAt)}`:''}</div>
                        )}
                        {(proofUrl||liveUrl) && (
                          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:2}}>
                            {proofUrl && <a href={proofUrl} target="_blank" rel="noreferrer"
                              style={{color:pl.color,textDecoration:'none',fontWeight:700}}>🔗 Proof</a>}
                            {liveUrl && <a href={liveUrl} target="_blank" rel="noreferrer"
                              style={{color:pl.color,textDecoration:'none',fontWeight:700,wordBreak:'break-all'}}>🔗 Live</a>}
                            {SCH_DASHBOARDS[pl.name] && (
                              <a href={SCH_DASHBOARDS[pl.name]} target="_blank" rel="noreferrer"
                                style={{color:'var(--text2)',textDecoration:'none'}}>Verify</a>
                            )}
                          </div>
                        )}
                        {!schedAt && !liveUrl && (
                          <span style={{fontStyle:'italic',opacity:0.6}}>Not yet scheduled</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div style={{display:'flex',gap:4,padding:'6px 10px',
                        borderTop:'1px solid var(--border)',marginTop:'auto'}}>
                        {currentTab === 'posted' ? (
                          /* Posted tab — only show live link */
                          liveUrl ? (
                            <a href={liveUrl} target="_blank" rel="noreferrer"
                              style={{flex:1,padding:'4px 6px',fontSize:'0.67rem',
                                fontWeight:700,border:'none',borderRadius:5,
                                background:pl.color,color:'#fff',cursor:'pointer',
                                textAlign:'center',textDecoration:'none',display:'block'}}>
                              ↗ View Live
                            </a>
                          ) : proofUrl ? (
                            <a href={proofUrl} target="_blank" rel="noreferrer"
                              style={{flex:1,padding:'4px 6px',fontSize:'0.67rem',
                                fontWeight:700,border:`1px solid ${pl.color}`,borderRadius:5,
                                color:pl.color,background:'transparent',
                                textAlign:'center',textDecoration:'none',display:'block'}}>
                              🔗 Proof
                            </a>
                          ) : (
                            <span style={{flex:1,padding:'4px 6px',fontSize:'0.62rem',
                              color:'var(--text3)',fontStyle:'italic',textAlign:'center'}}>
                              No link recorded
                            </span>
                          )
                        ) : (
                          /* Ready / Scheduled tabs — full action buttons */
                          <>
                            <button onClick={()=>handleLaunch(pl)} title={pl.help}
                              style={{flex:1,padding:'4px 6px',fontSize:'0.67rem',
                                fontWeight:700,border:'none',borderRadius:5,
                                background:pl.color,color:'#fff',cursor:'pointer'}}>
                              {state?'↗ Re-open':'🚀 Launch'}
                            </button>
                            <button onClick={()=>setBatchTarget(pl.id)}
                              style={{padding:'4px 8px',fontSize:'0.67rem',fontWeight:700,
                                border:`1px solid ${pl.color}`,color:pl.color,
                                background:'transparent',borderRadius:5,cursor:'pointer'}}>
                              ✎
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{padding:'12px 14px',
                background:'rgba(247,195,79,0.08)',
                border:'1px dashed rgba(247,195,79,0.5)',
                borderRadius:8,fontSize:'0.78rem',color:'#9a6800'}}>
                ⚠️ No launchable platforms assigned. Edit the Work Order to add platforms.
              </div>
            )}

            {orphans.length > 0 && (
              <div style={{marginTop:10,padding:'8px 12px',
                background:'var(--bg3)',border:'1px dashed var(--border2)',
                borderRadius:8,fontSize:'0.72rem',color:'var(--text2)'}}>
                <b>Manual:</b> {orphans.map(p=>(
                  <span key={p} style={{padding:'1px 8px',
                    background:'var(--bg2)',border:'1px solid var(--border)',
                    borderRadius:10,margin:'0 3px'}}>
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>

        </div>{/* /padding wrapper */}
      </div>{/* /card */}

      {lightbox && (
        <ImageLightbox src={creative?.image_data} name={creative?.image_name} onClose={()=>setLightbox(false)} />
      )}
      {batchTarget && (
        <BatchModal
          wo={wo}
          tiles={batchTarget==='__all__' ? tiles : tiles.filter(pl=>pl.id===batchTarget)}
          platRecs={platRecs}
          currentTab={woState}
          onClose={()=>setBatchTarget(null)}
          onSave={()=>{ setBatchTarget(null); setLoadingDetail(true);
            api.get(`/scheduling/${wo.id}`).then(r=>{
              const map={};
              (r.data.platforms||[]).forEach(p=>{
                const pl=findPlatformConfig(p.platform);
                const key=pl?pl.id:p.platform;
                map[key]={...p,state:p.state==='pending'?undefined:p.state};
              });
              setPlatRecs(map);
            }).finally(()=>{setLoadingDetail(false);onReload();});
          }}
        />
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Scheduling() {
  const { canTab } = useAuth();
  const [tab,       setTab]      = useState('ready');
  const [wos,       setWos]      = useState([]);
  const [clients,   setClients]  = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [yearF,     setYearF]    = useState('');
  const [monthsF,   setMonthsF]  = useState(new Set());
  const [showMonthDd,setShowMonthDd]= useState(false);
  const [clientF,   setClientF]  = useState('');
  const [woFilter,  setWoFilter]  = useState('');
  const [platF,     setPlatF]    = useState('');
  const [woStateMap,setWoStateMap]= useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (clientF) params.client_id = clientF;
      const [woRes, clRes] = await Promise.all([
        api.get('/scheduling', { params }),
        api.get('/clients'),
      ]);
      const data = woRes.data.data || woRes.data || [];
      setWos(Array.isArray(data) ? data : []);
      setClients(clRes.data.data || clRes.data || []);

      // Build state map from scheduling_platforms data already in the list response
      const sm = {};
      data.forEach(wo => {
        const platMap = {};
        (wo.scheduling_platforms || []).forEach(sp => {
          const pl = findPlatformConfig(sp.platform_name);
          const key = pl ? pl.id : sp.platform_name;
          platMap[key] = { ...sp, state: sp.state==='pending' ? undefined : sp.state };
        });
        sm[wo.id] = getWOState(wo, platMap);
      });
      setWoStateMap(sm);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientF]);

  useEffect(() => { load(); }, [load]);

  const counts = { ready:0, scheduled:0, posted:0 };
  wos.forEach(wo => { const s = woStateMap[wo.id] || 'ready'; counts[s]++; });

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1].map(String);
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const toggleMonth = m => setMonthsF(prev => { const s = new Set(prev); s.has(m) ? s.delete(m) : s.add(m); return s; });

  useEffect(() => {
    if (!showMonthDd) return;
    const h = e => { if (!e.target.closest('[data-month-dd]')) setShowMonthDd(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMonthDd]);

  const filtered = wos.filter(wo => {
    if (woFilter && !(wo.wo_number||'').toLowerCase().includes(woFilter.toLowerCase())) return false;
    if ((woStateMap[wo.id]||'ready') !== tab) return false;
    if (platF && !(wo.platforms||[]).some(p=>normStr(p)===normStr(platF))) return false;
    if (wo.event_date) {
      if (yearF && wo.event_date.slice(0, 4) !== yearF) return false;
      if (monthsF.size > 0) { const woMonth = parseInt(wo.event_date.split('-')[1], 10); if (!monthsF.has(woMonth)) return false; }
    }
    return true;
  });

  const allPlatforms = [...new Set(wos.flatMap(wo=>wo.platforms||[]))].sort();

  const TAB_CONFIG = [
    { id:'ready',     label:'🚀 Ready to Schedule', color:'#f7c34f' },
    { id:'scheduled', label:'🗓️ Scheduled',          color:'#3b7ef0' },
    { id:'posted',    label:'✅ Posted',              color:'#1db87a' },
  ];
  const EMPTY_MSGS = {
    ready:     { icon:'🚀', msg:'No work orders waiting to schedule',   sub:'Once creatives/copies are approved per workflow, WOs appear here.' },
    scheduled: { icon:'🗓️', msg:'Nothing scheduled yet',                sub:'WOs you schedule appear here until they go live.' },
    posted:    { icon:'✅', msg:'No posts recorded yet',                 sub:'Once you mark a platform Posted with its live URL, the WO moves here.' },
  };

  return (
    <div>
      {/* Sticky header */}
      <div style={{position:'sticky',top:'-24px',zIndex:30,background:'var(--bg)',margin:'-24px -24px 16px',padding:'24px 24px 0'}}>
        <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:'2px solid var(--border)'}}>
          {TAB_CONFIG.filter(t => { const cfg = (OPERATIONS_TABS.scheduling||[]).find(x=>x.id===t.id); return !cfg?.permissionKey || canTab(cfg.permissionKey); }).map(t => (
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:'8px 20px',fontSize:'0.82rem',fontWeight:700,border:'none',background:'none',cursor:'pointer',
                color:tab===t.id?t.color:'var(--text3)',
                borderBottom:tab===t.id?`3px solid ${t.color}`:'3px solid transparent',marginBottom:'-2px'}}>
              {t.label}
              {counts[t.id]>0 && (
                <span style={{marginLeft:4,padding:'1px 6px',background:`${t.color}22`,color:t.color,borderRadius:10,fontSize:'0.7rem',fontWeight:800}}>
                  {counts[t.id]}
                </span>
              )}
            </button>
          ))}

          {/* spacer */}
          <div style={{marginLeft:'auto'}} />
        </div>

        {/* Filters */}
        <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',display:'flex',alignItems:'center'}}>
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--text3)" strokeWidth="1.5" style={{position:'absolute',left:8,pointerEvents:'none'}}><circle cx="6.5" cy="6.5" r="4.5"/><line x1="10" y1="10" x2="14" y2="14"/></svg>
            <input value={woFilter||''} onChange={e=>setWoFilter(e.target.value)}
              placeholder="WO number..." style={{padding:'5px 10px',paddingLeft:26,width:130,fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)'}} />
            {woFilter && <span onClick={()=>setWoFilter('')} style={{position:'absolute',right:8,cursor:'pointer',color:'var(--text3)',fontSize:'0.8rem'}}>✕</span>}
          </div>
          <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={clientF} onChange={e=>setClientF(e.target.value)}>
            <option value="">All Clients</option>
            {clients.filter(c=>c.enabled!==false).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={platF} onChange={e=>setPlatF(e.target.value)}>
            <option value="">All Platforms</option>
            {allPlatforms.map(p=><option key={p}>{p}</option>)}
          </select>
          <select style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text)',cursor:'pointer',width:'auto'}} value={yearF} onChange={e=>setYearF(e.target.value)}>
            <option value="">All Years</option>
            {yearOptions.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          {/* Multi-month dropdown */}
          <div data-month-dd style={{position:'relative'}}>
            <button onClick={()=>setShowMonthDd(v=>!v)}
              style={{padding:'5px 10px',fontSize:'0.78rem',borderRadius:6,cursor:'pointer',
                display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap',
                background:monthsF.size>0?'#6c58f022':'var(--bg2)',
                border:`1px solid ${monthsF.size>0?'#6c58f0':'var(--border)'}`,
                color:monthsF.size>0?'#6c58f0':'var(--text)'}}>
              📅 {monthsF.size===0?'All Months':[...monthsF].sort((a,b)=>a-b).map(m=>MONTH_NAMES[m-1]).join(', ')}
              <span style={{fontSize:'0.6rem',marginLeft:2}}>▾</span>
            </button>
            {showMonthDd&&(
              <div style={{position:'absolute',top:'110%',left:0,zIndex:200,
                background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,
                boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:10,minWidth:200}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:4}}>
                  {MONTH_NAMES.map((name,i)=>{const m=i+1;const on=monthsF.has(m);return(
                    <button key={m} onClick={()=>toggleMonth(m)}
                      style={{padding:'5px 8px',fontSize:'0.73rem',fontWeight:on?700:500,
                        borderRadius:6,border:`1px solid ${on?'#6c58f0':'var(--border)'}`,
                        background:on?'#6c58f0':'var(--bg2)',
                        color:on?'#fff':'var(--text2)',cursor:'pointer'}}>
                      {name}
                    </button>
                  );})}
                </div>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:8,gap:6}}>
                  <button onClick={()=>setMonthsF(new Set())}
                    style={{flex:1,padding:'4px',fontSize:'0.68rem',borderRadius:6,cursor:'pointer',
                      border:'1px solid var(--border)',background:'var(--bg2)',color:'var(--text3)'}}>Clear</button>
                  <button onClick={()=>setShowMonthDd(false)}
                    style={{flex:1,padding:'4px',fontSize:'0.68rem',fontWeight:700,borderRadius:6,
                      cursor:'pointer',border:'none',background:'#6c58f0',color:'#fff'}}>Done</button>
                </div>
              </div>
            )}
          </div>
          <span style={{fontSize:'0.72rem',color:'var(--text3)',fontFamily:'Space Mono,monospace'}}>
            {filtered.length} of {wos.length} WOs
          </span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{textAlign:'center',padding:48,color:'var(--text3)'}}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:'center',padding:'60px 24px',background:'var(--bg2)',borderRadius:'var(--radius)',border:'1px dashed var(--border)'}}>
          <div style={{fontSize:'3rem',marginBottom:12,opacity:0.5}}>{EMPTY_MSGS[tab]?.icon}</div>
          <div style={{fontWeight:700,fontSize:'1.05rem',color:'var(--text2)',marginBottom:4}}>{EMPTY_MSGS[tab]?.msg}</div>
          <div style={{fontSize:'0.78rem',color:'var(--text3)'}}>{EMPTY_MSGS[tab]?.sub}</div>
        </div>
      ) : (
        filtered.map(wo => <SchedulingCard key={wo.id} wo={wo} onReload={load} currentTab={tab} />)
      )}
    </div>
  );
}